import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Loader2,
    X,
    ChevronLeft,
    ChevronRight,
    User
} from 'lucide-react'
import { format } from 'date-fns'
import { fr, enUS } from 'date-fns/locale'
import toast from 'react-hot-toast'
import ConfirmModal from '../../components/ConfirmModal'
import Select from 'react-select'
import './UsersPage.css'

export default function UsersPage() {
    const { t, i18n } = useTranslation()
    const { hasPermission } = useAuthStore()
    const dateLocale = i18n.language === 'fr' ? fr : enUS

    const [users, setUsers] = useState([])
    const [profils, setProfils] = useState([])
    const [equipes, setEquipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 })
    const [searchTerm, setSearchTerm] = useState('')

    const customStyles = {
        control: (base, state) => ({
            ...base,
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
            boxShadow: state.isFocused ? '0 0 0 2px var(--primary-500)' : 'none',
            '&:hover': {
                borderColor: 'var(--primary-500)'
            }
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            zIndex: 9999
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? 'var(--primary-500)' : state.isFocused ? 'var(--bg-tertiary)' : 'transparent',
            color: state.isSelected ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            ':active': {
                backgroundColor: 'var(--primary-600)'
            }
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: 'var(--bg-tertiary)',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: 'var(--text-primary)',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'var(--text-muted)',
            ':hover': {
                backgroundColor: 'var(--danger-500)',
                color: 'white',
            },
        }),
        input: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        })
    }

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({ open: false, user: null, loading: false })

    const [showModal, setShowModal] = useState(false)
    const [editingUser, setEditingUser] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [formData, setFormData] = useState({
        login: '',
        password: '',
        nom: '',
        prenom: '',
        profil_id: '',
        equipes: []
    })

    useEffect(() => {
        fetchUsers()
        fetchProfils()
        fetchEquipes()
    }, [pagination.page])

    const fetchUsers = async () => {
        setLoading(true)
        try {
            const response = await api.get('/users', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    search: searchTerm
                }
            })
            setUsers(response.data.data.users)
            setPagination(prev => ({ ...prev, ...response.data.data.pagination }))
        } catch (error) {
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    const fetchProfils = async () => {
        try {
            const response = await api.get('/profils', { params: { all: true } })
            setProfils(response.data.data.profils)
        } catch (error) {
            console.error('Error fetching profiles:', error)
        }
    }

    const fetchEquipes = async () => {
        try {
            const response = await api.get('/equipes', { params: { all: true } })
            setEquipes(response.data.data.equipes)
        } catch (error) {
            console.error('Error fetching teams:', error)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setPagination(prev => ({ ...prev, page: 1 }))
        fetchUsers()
    }

    const openAddModal = () => {
        setEditingUser(null)
        setFormData({
            login: '',
            password: '',
            nom: '',
            prenom: '',
            profil_id: '',
            equipes: []
        })
        setShowModal(true)
    }

    const openEditModal = (user) => {
        setEditingUser(user)
        setFormData({
            login: user.login,
            password: '',
            nom: user.nom,
            prenom: user.prenom,
            profil_id: user.profil_id._id,
            equipes: user.equipes?.map(e => e.id) || []
        })
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)

        try {
            const payload = { ...formData }

            // Format equipes
            payload.equipes = formData.equipes.map(id => ({ id }))

            // Remove password if editing and empty
            if (editingUser && !payload.password) {
                delete payload.password
            }

            if (editingUser) {
                await api.put(`/users/${editingUser._id}`, payload)
                toast.success(t('common.success'))
            } else {
                await api.post('/users', payload)
                toast.success(t('common.success'))
            }

            setShowModal(false)
            fetchUsers()
        } catch (error) {
            const data = error.response?.data
            if (data?.message === 'VALIDATION_FAILED' && data?.errors?.length > 0) {
                const firstError = data.errors[0]
                toast.error(t(`validation.${firstError.message}`, { defaultValue: t('validation.fixErrors') }))
            } else {
                // Try to translate as validation code, fallback to message or generic error
                const msg = data?.message
                const translated = msg ? t(`validation.${msg}`, { defaultValue: msg }) : t('common.error')
                toast.error(translated)
            }
        } finally {
            setModalLoading(false)
        }
    }

    const handleDelete = (user) => {
        setConfirmModal({ open: true, user, loading: false })
    }

    const confirmDelete = async () => {
        const user = confirmModal.user
        if (!user) return

        setConfirmModal(prev => ({ ...prev, loading: true }))
        try {
            await api.delete(`/users/${user._id}`)
            toast.success(t('common.success'))
            setConfirmModal({ open: false, user: null, loading: false })
            fetchUsers()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
            setConfirmModal(prev => ({ ...prev, loading: false }))
        }
    }

    return (
        <div className="users-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('users.title')}</h1>
                    <p className="page-subtitle">{pagination.total} {t('users.title').toLowerCase()}</p>
                </div>

                {hasPermission('ADD_USER') && (
                    <button className="btn btn-primary" onClick={openAddModal}>
                        <Plus size={18} />
                        {t('users.add')}
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="card">
                <div className="filters-bar">
                    <form onSubmit={handleSearch} className="search-form">
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                className="form-input search-input"
                                placeholder={t('common.search') + '...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </form>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                {loading ? (
                    <div className="table-loading">
                        <Loader2 size={32} className="spin" />
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{t('users.name')}</th>
                                        <th>{t('users.login')}</th>
                                        <th>{t('users.profile')}</th>
                                        <th>{t('users.teams')}</th>
                                        <th>{t('users.lastLogin')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted p-6">
                                                {t('common.noData')}
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr key={user._id}>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-avatar">
                                                            {user.prenom?.[0]}{user.nom?.[0]}
                                                        </div>
                                                        <strong>{user.prenom} {user.nom}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <code className="user-login">{user.login}</code>
                                                </td>
                                                <td>
                                                    <span className="badge badge-primary">
                                                        {user.profil_id?.nom_profil}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="teams-list">
                                                        {user.equipes?.slice(0, 2).map(eq => (
                                                            <span key={eq.id} className="badge badge-neutral">
                                                                {eq.nom}
                                                            </span>
                                                        ))}
                                                        {user.equipes?.length > 2 && (
                                                            <span className="badge badge-neutral">
                                                                +{user.equipes.length - 2}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {user.derniere_connexion
                                                        ? format(new Date(user.derniere_connexion), 'Pp', { locale: dateLocale })
                                                        : '-'
                                                    }
                                                </td>
                                                <td>
                                                    <div className="table-actions">
                                                        {hasPermission('MODIF_USER') && (
                                                            <button
                                                                className="btn btn-icon btn-ghost"
                                                                onClick={() => openEditModal(user)}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                        )}
                                                        {hasPermission('DELETE_USER') && user.profil_id?.code_profil !== 'SUPERADMIN' && (
                                                            <button
                                                                className="btn btn-icon btn-ghost"
                                                                onClick={() => handleDelete(user)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="table-pagination">
                                <span className="pagination-info">
                                    Page {pagination.page} {t('common.of')} {pagination.pages}
                                </span>
                                <div className="pagination">
                                    <button
                                        className="pagination-btn"
                                        disabled={pagination.page <= 1}
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button
                                        className="pagination-btn"
                                        disabled={pagination.page >= pagination.pages}
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingUser ? t('users.edit') : t('users.add')}
                            </h3>
                            <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('users.firstName')} *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.prenom}
                                            onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('users.name')} *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.nom}
                                            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('users.login')} *</label>
                                        <div className="input-with-icon">
                                            <User size={18} className="input-icon" />
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={formData.login}
                                                onChange={(e) => setFormData({ ...formData, login: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                                                required
                                                disabled={!!editingUser}
                                                placeholder={t('users.loginPlaceholder')}
                                            />
                                        </div>
                                        <small className="form-hint">{t('users.loginHint')}</small>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            {t('users.password')} {!editingUser && '*'}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required={!editingUser}
                                            placeholder={editingUser ? t('users.passwordPlaceholderEdit') : ''}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('users.profile')} *</label>
                                        <Select
                                            options={profils.map((profil) => ({ value: profil._id, label: profil.nom_profil }))}
                                            value={profils.find(p => p._id === formData.profil_id) ? { value: formData.profil_id, label: profils.find(p => p._id === formData.profil_id).nom_profil } : null}
                                            onChange={(selected) => setFormData({ ...formData, profil_id: selected ? selected.value : '' })}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={t('common.select')}
                                            styles={customStyles}
                                            required
                                            menuPortalTarget={document.body}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">{t('users.teams')}</label>
                                        <Select
                                            isMulti={profils.find(p => p._id === formData.profil_id)?.code_profil !== 'AGENT'}
                                            options={equipes.map(e => ({ value: e._id, label: e.nom_equipe }))}
                                            value={
                                                profils.find(p => p._id === formData.profil_id)?.code_profil === 'AGENT'
                                                    ? (formData.equipes.length > 0
                                                        ? { value: formData.equipes[0], label: equipes.find(e => e._id === formData.equipes[0])?.nom_equipe }
                                                        : null)
                                                    : equipes.filter(e => formData.equipes.includes(e._id)).map(e => ({ value: e._id, label: e.nom_equipe }))
                                            }
                                            onChange={(selected) => {
                                                const isAgent = profils.find(p => p._id === formData.profil_id)?.code_profil === 'AGENT';
                                                let newEquipes = [];
                                                if (isAgent) {
                                                    newEquipes = selected ? [selected.value] : [];
                                                } else {
                                                    newEquipes = selected ? selected.map(s => s.value) : [];
                                                }
                                                setFormData({ ...formData, equipes: newEquipes });
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={t('common.select')}
                                            styles={customStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal({ open: false, user: null, loading: false })}
                onConfirm={confirmDelete}
                title={t('users.deleteConfirm')}
                message={confirmModal.user ? `${confirmModal.user.prenom} ${confirmModal.user.nom}` : ''}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                loading={confirmModal.loading}
            />
        </div>
    )
}
